import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { BrandPsiShape } from './brand-mark.js';

/**
 * The brand loader (D-106; approved sample `frontend/docs/design/brand-loader-sample.html`).
 *
 * While `done` is false: brand green, a looping gold wave packet (three strokes, a breathing
 * Gaussian envelope, a slow drift) and a flickering probability cloud, with no logo and no text.
 * When `done` turns true: the packet collapses to the centre, the gold winged Ψ forms from the
 * centre with a soft glow, one light sweep crosses the mark, and «Grab & Go» fades in under it;
 * `onFinished` follows once the sequence has played. Reduced motion: the still Ψ with the slogan,
 * and `onFinished` at once when done.
 *
 * One requestAnimationFrame loop writes SVG attributes through refs (no re-render per frame).
 */
export interface BrandLoaderProps {
  readonly done: boolean;
  readonly onFinished?: () => void;
  /** The visually hidden status line (default «در حال ورود…»). */
  readonly label?: string;
}

/** The packet is shown for at least this long before it may collapse (sample: 0.6 s). */
export const BRAND_LOADER_MIN_LOADING_MS = 600;
/** `onFinished` runs this long after loading ends: the Ψ, the slogan and the sweep have played. */
export const BRAND_LOADER_FINISH_MS = 1400;

const SVG_SIZE = 800;
const CENTRE_X = 400;
const MARK_CENTRE_Y = 430;
const WAVE_CENTRE_Y = 405;
const CLOUD_CENTRE_Y = 410;
const WAVE_STEPS = 140;
const DOT_COUNT = 30;
const GOLD = '#d2b956';

const STROKES = [
  { amplitude: 78, opacity: 0.95, speed: 9, wavelength: 34, width: 6 },
  { amplitude: 52, opacity: 0.45, speed: 6.5, wavelength: 22, width: 3 },
  { amplitude: 40, opacity: 0.28, speed: 4.5, wavelength: 46, width: 2 },
] as const;

/** The Ψ (viewBox 780 0 1160 900) placed in the 800×800 box with xMidYMid meet. */
const PSI_SCALE = SVG_SIZE / 1160;
const PSI_TRANSFORM = `translate(0 ${((SVG_SIZE - 900 * PSI_SCALE) / 2).toFixed(3)}) scale(${PSI_SCALE.toFixed(6)}) translate(-780 0)`;

interface Dot {
  readonly x: number;
  readonly y: number;
  readonly r: number;
}

function createDots(): Dot[] {
  return Array.from({ length: DOT_COUNT }, (_, i) => {
    const angle = (i / DOT_COUNT) * Math.PI * 2 + Math.random() * 0.5;
    const radius = 70 + Math.random() * 190;
    return {
      r: 2 + Math.random() * 3.5,
      x: CENTRE_X + Math.cos(angle) * radius,
      y: CLOUD_CENTRE_Y + Math.sin(angle) * radius * 0.55,
    };
  });
}

const ease = (t: number) => (t <= 0 ? 0 : t >= 1 ? 1 : 1 - Math.pow(1 - t, 3));
const clamp = (t: number) => Math.max(0, Math.min(1, t));

/**
 * Reduced motion, also when the environment cannot answer: every supported browser has
 * `matchMedia`, so its absence means a non-browser host (server rendering, jsdom tests) where the
 * still mark is the right choice and the app opens at once.
 */
function prefersReducedMotion(): boolean {
  return (
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function' ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function now(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now();
}

export function BrandLoader({ done, label = 'در حال ورود…', onFinished }: BrandLoaderProps) {
  const id = useId().replace(/:/g, '');
  const [reduced] = useState(prefersReducedMotion);
  const dots = useMemo(createDots, []);

  const startRef = useRef(now());
  /** Seconds since mount (animation clock) at which loading ended; null while loading. */
  const doneAtRef = useRef<number | null>(null);
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  const pathRefs = useRef<(SVGPathElement | null)[]>([]);
  const dotRefs = useRef<(SVGCircleElement | null)[]>([]);
  const revealRef = useRef<SVGCircleElement | null>(null);
  const glowRef = useRef<SVGCircleElement | null>(null);
  const sweepRef = useRef<SVGRectElement | null>(null);
  const sloganRef = useRef<HTMLDivElement | null>(null);

  // Loading ends: fix the collapse moment (after the minimum packet time), then finish.
  useEffect(() => {
    if (!done) {
      doneAtRef.current = null;
      return;
    }
    if (reduced) {
      onFinishedRef.current?.();
      return;
    }
    const elapsed = (now() - startRef.current) / 1000;
    const doneAt = Math.max(elapsed, BRAND_LOADER_MIN_LOADING_MS / 1000);
    doneAtRef.current = doneAt;
    const timer = setTimeout(
      () => onFinishedRef.current?.(),
      (doneAt - elapsed) * 1000 + BRAND_LOADER_FINISH_MS,
    );
    return () => clearTimeout(timer);
  }, [done, reduced]);

  // The single animation loop.
  useEffect(() => {
    if (reduced || typeof requestAnimationFrame !== 'function') {
      return;
    }
    let handle = 0;
    const frame = (time: number) => {
      const t = (time - startRef.current) / 1000;
      const doneAt = doneAtRef.current;
      const sinceDone = doneAt === null ? -1 : t - doneAt;
      const collapse = doneAt === null ? 0 : ease(sinceDone / 0.55);
      const appear = ease(t / 0.4);

      // Loading: a wave packet that keeps travelling and breathing.
      const breathe = 0.82 + 0.18 * Math.sin(t * 2.2);
      const sigma = 230 * breathe * (1 - 0.88 * collapse) + 16;
      const drift = 70 * Math.sin(t * 1.1) * (1 - collapse);
      STROKES.forEach((stroke, k) => {
        const path = pathRefs.current[k];
        if (!path) {
          return;
        }
        let d = '';
        for (let s = 0; s <= WAVE_STEPS; s++) {
          const x = 20 + (760 * s) / WAVE_STEPS;
          const envelope = Math.exp(-Math.pow((x - CENTRE_X - drift) / sigma, 2));
          const y =
            WAVE_CENTRE_Y +
            stroke.amplitude *
              appear *
              (1 - collapse) *
              envelope *
              Math.sin((x - CENTRE_X) / stroke.wavelength - t * stroke.speed + k * 1.7);
          d += `${s ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`;
        }
        path.setAttribute('d', d);
        path.setAttribute(
          'opacity',
          (stroke.opacity * appear * Math.pow(1 - collapse, 2.5)).toFixed(3),
        );
      });

      // Probability cloud: flickering points around the packet.
      const keep = 1 - collapse;
      dots.forEach((dot, i) => {
        const circle = dotRefs.current[i];
        if (!circle) {
          return;
        }
        circle.setAttribute(
          'cx',
          (CENTRE_X + (dot.x - CENTRE_X) * keep + drift * 0.4 * keep).toFixed(1),
        );
        circle.setAttribute('cy', (CLOUD_CENTRE_Y + (dot.y - CLOUD_CENTRE_Y) * keep).toFixed(1));
        circle.setAttribute(
          'opacity',
          ((0.12 + 0.55 * Math.abs(Math.sin(t * 5 + i * 1.3))) * appear * keep).toFixed(3),
        );
      });

      // Loaded: Ψ forms from the centre with a glow, then the slogan and one sweep.
      revealRef.current?.setAttribute('r', (520 * collapse).toFixed(1));
      const glowT = doneAt === null ? 0 : clamp((sinceDone - 0.05) / 0.9);
      glowRef.current?.setAttribute('r', (270 * ease(glowT)).toFixed(1));
      glowRef.current?.setAttribute(
        'opacity',
        (glowT === 0 ? 0 : glowT < 0.35 ? glowT / 0.35 : 1 - (glowT - 0.35) / 0.65).toFixed(3),
      );
      const slogan = sloganRef.current;
      if (slogan) {
        const shown = doneAt === null ? 0 : ease((sinceDone - 0.45) / 0.5);
        slogan.style.opacity = shown.toFixed(3);
        slogan.style.transform = `translateY(${(8 * (1 - shown)).toFixed(1)}px)`;
      }
      sweepRef.current?.setAttribute(
        'x',
        doneAt !== null && sinceDone > 0.6 && sinceDone < 1.6
          ? (-300 + ((sinceDone - 0.6) / 1.0) * 1400).toFixed(1)
          : '-300',
      );
      handle = requestAnimationFrame(frame);
    };
    handle = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(handle);
  }, [dots, reduced]);

  const glowId = `saay-loader-glow-${id}`;
  const revealId = `saay-loader-reveal-${id}`;
  const sweepId = `saay-loader-sweep-${id}`;
  const maskId = `saay-loader-mask-${id}`;
  const showSlogan = reduced || done;

  return (
    <div
      className={reduced ? 'saay-brand-loader saay-brand-loader--still' : 'saay-brand-loader'}
      data-done={done ? 'true' : 'false'}
      role="status"
    >
      <span className="saay-visually-hidden">{label}</span>
      <div className="saay-brand-loader__mark">
        <svg
          aria-hidden="true"
          className="saay-brand-loader__art"
          focusable="false"
          viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
          xmlns="http://www.w3.org/2000/svg"
        >
          {reduced ? (
            <g transform={PSI_TRANSFORM}>
              <BrandPsiShape fill={GOLD} />
            </g>
          ) : (
            <>
              <defs>
                <radialGradient cx="50%" cy="48%" id={glowId} r="50%">
                  <stop offset="0" stopColor="#fff6c8" stopOpacity=".9" />
                  <stop offset=".35" stopColor={GOLD} stopOpacity=".35" />
                  <stop offset="1" stopColor={GOLD} stopOpacity="0" />
                </radialGradient>
                <clipPath id={revealId}>
                  <circle cx={CENTRE_X} cy={MARK_CENTRE_Y} r="0" ref={revealRef} />
                </clipPath>
                <linearGradient id={sweepId} x1="0" x2="1" y1="0" y2="0">
                  <stop offset="0" stopColor="#fff" stopOpacity="0" />
                  <stop offset=".5" stopColor="#fffbe6" stopOpacity=".75" />
                  <stop offset="1" stopColor="#fff" stopOpacity="0" />
                </linearGradient>
                <mask
                  height={SVG_SIZE}
                  id={maskId}
                  maskUnits="userSpaceOnUse"
                  width={SVG_SIZE}
                  x="0"
                  y="0"
                >
                  <g transform={PSI_TRANSFORM}>
                    <BrandPsiShape fill="#fff" />
                  </g>
                </mask>
              </defs>
              <circle
                cx={CENTRE_X}
                cy={MARK_CENTRE_Y}
                fill={`url(#${glowId})`}
                opacity="0"
                r="0"
                ref={glowRef}
              />
              <g data-part="cloud">
                {dots.map((dot, i) => (
                  <circle
                    cx={dot.x.toFixed(1)}
                    cy={dot.y.toFixed(1)}
                    fill={GOLD}
                    key={i}
                    opacity="0"
                    r={dot.r.toFixed(2)}
                    ref={(node) => {
                      dotRefs.current[i] = node;
                    }}
                  />
                ))}
              </g>
              <g data-part="waves" fill="none" stroke={GOLD} strokeLinecap="round">
                {STROKES.map((stroke, k) => (
                  <path
                    d=""
                    key={k}
                    opacity="0"
                    ref={(node) => {
                      pathRefs.current[k] = node;
                    }}
                    strokeWidth={stroke.width}
                  />
                ))}
              </g>
              <g clipPath={`url(#${revealId})`} data-part="psi">
                <g transform={PSI_TRANSFORM}>
                  <BrandPsiShape fill={GOLD} />
                </g>
              </g>
              <g mask={`url(#${maskId})`}>
                <rect
                  fill={`url(#${sweepId})`}
                  height={SVG_SIZE}
                  ref={sweepRef}
                  width="260"
                  x="-300"
                  y="0"
                />
              </g>
            </>
          )}
        </svg>
        {showSlogan ? (
          <div aria-hidden="true" className="saay-brand-loader__slogan" lang="en" ref={sloganRef}>
            Grab &amp; Go
          </div>
        ) : null}
      </div>
    </div>
  );
}
